import { Button } from '@/components/ui/button'
import { cryptoCurrencyDataSource } from '@/constants/datasource'
import { acquireWriteOperation } from '@/features/portfolio/states/writeOperation'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/utils'
import { getPortfolioFromLocalStorage } from '@/localStorage'
import type { PortfolioPageData } from '@/types'
import { showErrorToast } from '@/utils/error'
import {
  calculateTransactionPlByWacb,
  createAssetBalancesForWacb,
  createTransactions,
} from '@pkg/accounting'
import { generateAccountingTransactionList } from '@pkg/analyze'
import { fiatCurrencies } from '@pkg/basic'
import { MixIcon } from '@radix-ui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { Loader2 } from 'lucide-react'
import { useMemo } from 'react'
import {
  isRequiredCalculationAtom,
  updateIsRequiredCalculationAtom,
} from '../../states/calculate'
import {
  calculationProgressAtom,
  exchangeApiProgressesAtom,
  progressesAtom,
} from '../../states/progress'
import type { CalculationProgress, ExchangeApiProgress } from '../../types'

const UpdatingTransactionToast = (
  props: Pick<
    PortfolioPageData,
    'readFromDatabase' | 'writeToDatabase' | 'readonly'
  >,
) => {
  const { readonly, readFromDatabase, writeToDatabase } = props
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [progresses] = useAtom(progressesAtom)
  const [exchangeApiProgressRecord] = useAtom(exchangeApiProgressesAtom)
  const exchangeApiProgress = Object.values(exchangeApiProgressRecord).filter(
    (progress): progress is ExchangeApiProgress => progress !== undefined,
  )
  const [calculationProgress, setCalculationProgress] = useAtom(
    calculationProgressAtom,
  )
  const [isRequiredCalculation] = useAtom(isRequiredCalculationAtom)
  const [, updateIsRequiredCalculation] = useAtom(
    updateIsRequiredCalculationAtom,
  )
  const calculate = async () => {
    if (!isRequiredCalculation) {
      return
    }
    if (writeToDatabase === undefined) {
      return
    }
    const portfolio = getPortfolioFromLocalStorage()
    if (portfolio === undefined) {
      return
    }
    const lease = acquireWriteOperation({
      portfolioId: portfolio.uuid,
      type: 'accounting-calculation',
    })
    setCalculationProgress({
      size: 1,
      done: 0,
      range: 1,
      synced: 0,
    })
    try {
      await queryClient.cancelQueries({ queryKey: ['period-pl'] })
      await generateAccountingTransactionList({
        readFromDatabase,
        writeToDatabase,
        fiats: fiatCurrencies,
        cryptoCurrencyDataSource,
        createTransactions: (args) => Promise.resolve(createTransactions(args)),
        createAssetBalancesForWacb: (args) =>
          Promise.resolve(createAssetBalancesForWacb(args)),
        calculateTransactionPlByWacb: (args) =>
          Promise.resolve(calculateTransactionPlByWacb(args)),
        onUpdateProgress: (change) => {
          setCalculationProgress((prev) =>
            prev === undefined
              ? {
                  size: 1,
                  done: 0,
                  range: 1,
                  synced: 0,
                  ...change,
                }
              : {
                  ...prev,
                  ...change,
                },
          )
        },
      })
      await updateIsRequiredCalculation({ readFromDatabase })
    } catch (error) {
      showErrorToast({
        error,
        toast,
      })
    } finally {
      lease.release()
      setCalculationProgress(undefined)
      await queryClient.invalidateQueries({ queryKey: ['period-pl'] })
    }
  }

  return (
    <UI
      isHidden={
        readonly ||
        progresses.length > 0 ||
        exchangeApiProgress.length > 0 ||
        !isRequiredCalculation
      }
      progress={calculationProgress}
      onCalculate={calculate}
    />
  )
}

const UI = ({
  isHidden,
  progress,
  onCalculate,
}: {
  readonly isHidden: boolean
  readonly progress: CalculationProgress | undefined
  readonly onCalculate: () => void
}) => {
  const percent = useMemo(
    () =>
      progress === undefined
        ? 0
        : Math.max(
            (progress.synced / Math.max(progress.range, progress.synced, 1) +
              progress.done) /
              progress.size -
              0.01,
            0,
          ) * 100,
    [progress],
  )

  return (
    <div
      className={cn(
        'absolute top-1 right-0 left-0 z-50 mx-auto inline-block w-80 duration-500',
        isHidden && 'translate-y-[-64px]',
      )}
    >
      <div className="flex w-full items-center justify-between space-x-4 rounded-full border border-primary/75 bg-background px-4 py-2 shadow">
        <p className="font-semibold text-muted-foreground text-sm">
          損益計算が未完了です
        </p>
        <Button
          size="sm"
          disabled={progress !== undefined}
          onClick={onCalculate}
          className="h-auto rounded-full py-1.5"
        >
          {progress === undefined ? (
            <MixIcon />
          ) : (
            <Loader2 className="animate-spin" />
          )}
          <span>
            {progress === undefined ? '計算する' : `${Math.floor(percent)} %`}
          </span>
        </Button>
      </div>
    </div>
  )
}

export default UpdatingTransactionToast
